import Select from "@components/common/Select";
import { FieldError } from "@components/form/FieldError";
import { isValidPhoneNumber } from "libphonenumber-js";
import theme from "@theme";
import {
  Checkbox,
  Datepicker,
  Heading,
  Icon,
  Input,
  Modal,
  Radio,
  RadioGroup,
  Spinner,
} from "@thetreep/ui";
import {
  Control,
  Controller,
  useController,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { DAP } from "@gen/dap/v1/dap_pb";
import { CardForm } from "./CardForm";
import {
  CardType,
  DisabilityType,
  WheelchairType,
} from "@gen/travel/v1/catalog_pb";
import { Card, Passenger } from "@services/travel";
import { onNumberChange } from "@utils/form";
import { DisabilityCard, Gender } from "@gen/travel/v1/common_pb";
import { PhoneInput } from "@components/common/PhoneInput";
import { BannerMessage } from "@components/common/BannerMessage";

export interface PassengerFormProps {
  control: Control<Passenger>;
  dap: DAP;
  cardTypes: CardType[];
  wheelchairTypes: WheelchairType[];
  disabilityTypes: DisabilityType[];
  // is the display for main form or for additional passenger?
  additionalPassenger?: boolean;
  canBeACompanion: boolean;
}

const PassengerForm = forwardRef(function PassengerForm(
  {
    control,
    dap,
    cardTypes,
    wheelchairTypes,
    disabilityTypes,
    additionalPassenger,
    canBeACompanion,
  }: PassengerFormProps,
  ref,
) {
  const { t } = useTranslation();

  // cards
  const [isCardFormOpen, setIsCardFormOpen] = useState(false);
  const {
    fields: cards,
    append,
    remove,
    update,
  } = useFieldArray({
    control,
    name: "cards",
  });
  // disability
  const { field: disabled } = useController({ control, name: "disability" });
  const { field: hasCompanionFare } = useController({
    control,
    name: "hasCompanionFare",
  });
  const formRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => ({
    scrollToFormError: () => {
      formRef.current?.scrollIntoView({ behavior: "smooth" });
    },
  }));

  const IS_COMPANION =
    typeof hasCompanionFare.value !== "undefined" &&
    hasCompanionFare.value !== null;

  const { field: gender } = useController({ control, name: "gender" });

  const { control: genderControl, watch } = useForm<{
    realGender: Gender;
    providerGender: Gender.MALE | Gender.FEMALE;
  }>({
    defaultValues: {
      realGender: Gender.MALE,
      providerGender: Gender.MALE,
    },
  });

  useEffect(() => {
    const subscription = watch((values) => {
      if (values.realGender === Gender.NON_BINARY) {
        gender.onChange(values.providerGender);
      } else {
        gender.onChange(values.realGender);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  const realGender = watch("realGender");

  useEffect(() => {
    if (!canBeACompanion) {
      hasCompanionFare.onChange(null);
    }
  }, [canBeACompanion]);

  function dateToString(date: Date | undefined): string | undefined {
    if (!date) {
      return;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  function stringToDate(dateString: string | undefined): Date | undefined {
    if (!dateString) {
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return;
    }
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day); // Months are 0-indexed
    return date;
  }

  function parseGender(gender: string): Gender {
    switch (gender) {
      case Gender.MALE.toString():
        return Gender.MALE;
      case Gender.FEMALE.toString():
        return Gender.FEMALE;
      case Gender.NON_BINARY.toString():
        return Gender.NON_BINARY;
      default:
        return Gender.UNSPECIFIED;
    }
  }

  return (
    <>
      <div>
        <div
          className="
            w-full
            bg-neutral-50 p-8 rounded-md
            flex flex-col gap-4"
          ref={formRef}
        >
          {!additionalPassenger && (
            <>
              <Heading>{t("search.passenger.mainPassenger")}</Heading>
              <div className="flex items-center gap-2 text-grey-blue-500 text-xs">
                <Icon
                  src="information_line"
                  size={16}
                  color={theme.colors["grey-blue"][500]}
                />
                <span>{t("search.passenger.info")}</span>
              </div>
              <div className="border-t border-light-blue-200 my-2"></div>
            </>
          )}

          {!cardTypes || !wheelchairTypes || !disabilityTypes ? (
            <div className="w-full h-full flex items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <>
              {!additionalPassenger && (
                <>
                  <div className="flex flex-col gap-2 col-span-2">
                    <span className="font-jakarta text-xl text-neutral-900">
                      {t("search.passenger.personalInfo.heading")}
                    </span>
                    <span className="text-xs text-text-strong">
                      {t("search.passenger.personalInfo.info")}
                    </span>
                  </div>
                </>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Controller
                    control={control}
                    name="lastName"
                    rules={{
                      required: additionalPassenger
                        ? t("form.lastName.required")
                        : false,
                    }}
                    render={({ field, fieldState }) => (
                      <Input
                        overrides={{
                          Input: {
                            props: {
                              "data-testid": "search-passenger-lastname",
                            },
                          },
                        }}
                        type="text"
                        placeholder="Doe"
                        disabled={!additionalPassenger}
                        required={additionalPassenger}
                        value={
                          additionalPassenger ? field.value || "" : dap.lastName
                        }
                        onChange={field.onChange}
                        error={fieldState.invalid}
                        errorMsg={fieldState?.error?.message}
                        label={`${t("search.passenger.lastname")}  ${
                          additionalPassenger ? " *" : ""
                        }`}
                      />
                    )}
                  />
                </div>
                <div>
                  <Controller
                    control={control}
                    name="firstName"
                    rules={{
                      required: additionalPassenger
                        ? t("form.firstName.required")
                        : false,
                    }}
                    render={({ field, fieldState }) => (
                      <Input
                        overrides={{
                          Input: {
                            props: {
                              "data-testid": "search-passenger-firstname",
                            },
                          },
                        }}
                        type="text"
                        placeholder="John"
                        disabled={!additionalPassenger}
                        value={
                          additionalPassenger
                            ? field.value || ""
                            : dap.firstName
                        }
                        onChange={field.onChange}
                        error={fieldState.invalid}
                        errorMsg={fieldState?.error?.message}
                        label={`${t("search.passenger.firstname")}  ${
                          additionalPassenger ? " *" : ""
                        }`}
                      />
                    )}
                  />
                </div>
                <div className="flex flex-col justify-between">
                  <Controller
                    control={genderControl}
                    name="realGender"
                    rules={{
                      required: t("form.gender.required"),
                    }}
                    render={({ field, fieldState }) => (
                      <div className="h-full flex flex-col justify-between">
                        <span className="font-bold text-xs text-text-strong">
                          {`${t("form.gender.label")} *`}
                        </span>
                        <div className="h-full flex flex-col justify-center flex-1">
                          <RadioGroup
                            align="horizontal"
                            value={field.value?.toString()}
                            onChange={(e) =>
                              field.onChange(parseGender(e.currentTarget.value))
                            }
                            error={fieldState.invalid}
                          >
                            <Radio value={Gender.MALE.toString()}>
                              {t("form.gender.male")}
                            </Radio>
                            <Radio value={Gender.FEMALE.toString()}>
                              {t("form.gender.female")}
                            </Radio>
                            <Radio value={Gender.NON_BINARY.toString()}>
                              {t("form.gender.nonBinary")}
                            </Radio>
                          </RadioGroup>
                        </div>
                        <FieldError error={fieldState.error?.message} />
                      </div>
                    )}
                  />
                </div>
                <div>
                  <Controller
                    control={control}
                    name="birthDate"
                    rules={{
                      required: t("form.birthDate.required"),
                      pattern: {
                        message: t("form.birthDate.invalid"),
                        value:
                          /^(19[0-9]{2}|20[0-2][0-9])-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/,
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <div className="[&_svg]:max-h-4 [&_svg]:mt-1">
                        <Datepicker
                          overrides={{
                            InputWrapper: {
                              props: {
                                "data-testid": "search-passenger-birthdate",
                              },
                            },
                          }}
                          placeholder={t("form.birthDate.placeholder")}
                          value={stringToDate(field.value)}
                          onChange={(e) =>
                            field.onChange(dateToString(e.date as Date))
                          }
                          required
                          error={fieldState.invalid}
                          errorMsg={fieldState?.error?.message}
                          label={`${t("search.passenger.birthdate")} *`}
                        />
                      </div>
                    )}
                  />
                </div>
                {realGender === Gender.NON_BINARY && (
                  <>
                    <BannerMessage className="col-span-2" type="warning">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: t("search.passenger.nonBinaryDisclaimer"),
                        }}
                      />
                    </BannerMessage>
                    <Controller
                      control={genderControl}
                      name="providerGender"
                      rules={{
                        required: t("form.gender.required"),
                      }}
                      render={({ field, fieldState }) => (
                        <div className="h-full flex flex-col justify-between col-span-2 gap-4">
                          <span className="font-bold text-xs text-text-strong">
                            {`${t("form.gender.label")} *`}
                          </span>
                          <div className="h-full flex flex-col justify-center flex-1">
                            <RadioGroup
                              align="horizontal"
                              value={field.value?.toString()}
                              onChange={(e) =>
                                field.onChange(
                                  parseGender(e.currentTarget.value),
                                )
                              }
                              error={fieldState.invalid}
                            >
                              <Radio value={Gender.MALE.toString()}>
                                {t("form.gender.male")}
                              </Radio>
                              <Radio value={Gender.FEMALE.toString()}>
                                {t("form.gender.female")}
                              </Radio>
                            </RadioGroup>
                          </div>
                          <FieldError error={fieldState.error?.message} />
                        </div>
                      )}
                    />
                  </>
                )}
                <div>
                  <Controller
                    control={control}
                    name="email"
                    rules={{
                      required: additionalPassenger
                        ? false
                        : t("form.email.required"),
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t("form.email.invalid"),
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <Input
                        overrides={{
                          Input: {
                            props: {
                              "data-testid": "search-passenger-email",
                            },
                          },
                        }}
                        type="text"
                        value={field.value || ""}
                        onChange={field.onChange}
                        required={!additionalPassenger}
                        error={fieldState.invalid}
                        errorMsg={fieldState?.error?.message}
                        placeholder={t("form.email.placeholder")}
                        label={
                          t("search.passenger.email") +
                          (additionalPassenger ? "" : " *")
                        }
                      />
                    )}
                  />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-xs text-text-strong">
                    {t("search.passenger.phone")}
                    {additionalPassenger ? "" : " *"}
                  </span>
                  <Controller
                    control={control}
                    name="phoneNumber"
                    rules={
                      additionalPassenger
                        ? undefined
                        : {
                            required: t("form.phone.required"),
                            validate: (value: string) =>
                              isValidPhoneNumber(value) ||
                              t("form.phone.invalid"),
                          }
                    }
                    render={({ field, fieldState }) => (
                      <PhoneInput
                        value={field.value}
                        onChange={field.onChange}
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                </div>

                <div className="flex items-center justify-between w-full md:col-span-2">
                  <span className="">
                    {t("search.passenger.disability.heading")}
                  </span>
                  <Checkbox
                    checkmarkType="toggle"
                    checked={!!disabled.value}
                    onChange={() =>
                      disabled.value
                        ? disabled.onChange(null)
                        : disabled.onChange({})
                    }
                  />
                </div>
                {!!disabled.value && (
                  <>
                    <div>
                      <span className="font-bold text-xs">
                        {t("search.passenger.disability.disability")}
                      </span>
                      <Controller
                        name="disability.type"
                        control={control}
                        render={({ field }) => (
                          <Select
                            className="mt-2"
                            value={field.value}
                            onChange={field.onChange}
                            options={disabilityTypes.map((d) => ({
                              label: d.name,
                              value: d.code,
                            }))}
                          />
                        )}
                      />
                    </div>
                    <WheelchairForm
                      control={control}
                      wheelchairTypes={wheelchairTypes}
                    />
                  </>
                )}
              </div>

              {canBeACompanion && (
                <Controller
                  name="hasCompanionFare"
                  control={control}
                  render={({ field }) => (
                    <>
                      <div className="flex items-center justify-between w-full md:col-span-2">
                        <span>{t("search.passenger.companion.heading")}</span>
                        <Checkbox
                          checkmarkType="toggle"
                          checked={IS_COMPANION}
                          onChange={(event) => {
                            if (event.target.checked) {
                              field.onChange(DisabilityCard.UNSPECIFIED);
                            } else {
                              field.onChange(null);
                            }
                          }}
                        />
                      </div>
                      {IS_COMPANION && (
                        <div className="w-full md:col-span-2">
                          <Select
                            className="mt-2"
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={t(
                              "search.passenger.companion.placeholder",
                            )}
                            options={[
                              {
                                label: t(
                                  "search.passenger.companion.unspecified",
                                ),
                                value: DisabilityCard.UNSPECIFIED,
                              },
                              {
                                label: t(
                                  "search.passenger.companion.disability_80_percent",
                                ),
                                value: DisabilityCard.DISABILITY_80_PERCENT,
                              },
                              {
                                label: t(
                                  "search.passenger.companion.need_third_person",
                                ),
                                value: DisabilityCard.NEED_THIRD_PERSON,
                              },
                              {
                                label: t(
                                  "search.passenger.companion.blindness_green_star",
                                ),
                                value: DisabilityCard.BLINDNESS_GREEN_STAR,
                              },
                            ]}
                          />
                        </div>
                      )}
                    </>
                  )}
                />
              )}

              {!additionalPassenger && (
                <>
                  <div className="border-t border-light-blue-200 my-2"></div>

                  <span className="font-jakarta text-xl text-neutral-900">
                    {t("search.passenger.card.heading")}
                  </span>
                </>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                {cards.map((c, i) => (
                  <CardDisplay
                    card={c}
                    cardTypes={cardTypes}
                    key={i}
                    onEdit={(c) => update(i, c)}
                    onDelete={() => remove(i)}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setIsCardFormOpen(true)}
                  className={`                    border border-cnam-blue border-dashed rounded-md
                    flex items-center justify-center
                    hover:bg-cnam-light-100 active:bg-cnam-light-200
                    ${additionalPassenger ? "col-span-2 h-[80px]" : "h-[180px]"}
                    `}
                >
                  <div className="flex items-center gap-2">
                    <Icon
                      src="add_line"
                      size={18}
                      color={theme.colors.cnam.blue.DEFAULT}
                    />
                    <span className="text-cnam-blue font-bold">
                      {t("search.passenger.card.add")}
                    </span>
                  </div>
                </button>
              </div>

              {dap.isCorsicanFaresActivated && (
                <div className="flex flex-col gap-8 border-t border-light-blue-200 pt-4 mt-4">
                  <div>
                    <div className="text-text-strong font-jakarta text-[14px] lg:text-[20px] mb-4">
                      {t("search.passenger.residentCorsicaRate")}
                    </div>
                    <div className="flex gap-2 text-grey-blue-500">
                      <Icon size={16} src="information_line" color="#495471" />
                      <p className="text-xs lg:text-sm">
                        {t("search.passenger.accreditationRequirement")}{" "}
                        <a
                          className=" underline"
                          href="https://accreditation-resident.com/newspring/public/portal/XK/welcome"
                        >
                          {t("search.passenger.residentCorsicaRateLink")}
                        </a>
                      </p>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Controller
                      control={control}
                      name="corsicaResidentNumber"
                      render={({ field, fieldState }) => (
                        <Input
                          type="text"
                          value={field.value || ""}
                          onChange={field.onChange}
                          error={fieldState.invalid}
                          errorMsg={fieldState?.error?.message}
                          placeholder={t("form.accreditation.placeholder")}
                          label={t("search.passenger.accreditation")}
                        />
                      )}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <CardModal
        isOpen={isCardFormOpen}
        cardTypes={cardTypes}
        onClose={() => setIsCardFormOpen(false)}
        onSubmit={(c) => {
          append(c);
          setIsCardFormOpen(false);
        }}
      />
    </>
  );
});

export default PassengerForm;

interface CardProps {
  card: Card;
  cardTypes: CardType[];
  onEdit: (c: Card) => void;
  onDelete?: () => void;
}

function CardDisplay({ card, cardTypes, onEdit, onDelete }: CardProps) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  return (
    <>
      <div className="border border-grey-blue-300 rounded-md h-[180px] w-[424px] flex flex-col">
        <div className="flex gap-2 items-center bg-grey-blue-500 rounded-t-md p-2">
          <Icon src="coupon_line" size={24} color="white" />
          <span className="text-white font-jakarta font-bold">
            {t("search.passenger.card.travelCard")}
          </span>
        </div>
        <div className="flex flex-col justify-between p-6 h-full">
          <div className="flex justify-between">
            <div className="font-jakarta">
              {cardTypes.find((c) => c.code === card.type)?.name || card.type}
            </div>
            <button
              type="button"
              className="hover:scale-105 active:scale-110"
              onClick={() => setIsEditing(true)}
            >
              <Icon src="pencil_line" size={24} color="black" />
            </button>
          </div>
          <div className="font-jakarta font-bold text-xl">{card.number}</div>
          <div className="flex justify-between">
            <div>{`${t("search.passenger.card.expireAt")} ${
              card.expirationDate
            }`}</div>
            <button
              type="button"
              className="hover:scale-105 active:scale-110"
              onClick={onDelete}
            >
              <Icon src="trash" size={24} color="black" />
            </button>
          </div>
        </div>
      </div>
      <CardModal
        isOpen={isEditing}
        card={card}
        cardTypes={cardTypes}
        onClose={() => setIsEditing(false)}
        onSubmit={onEdit}
      />
    </>
  );
}

interface CardModalProps {
  isOpen: boolean;
  card?: Card;
  cardTypes: CardType[];
  onSubmit: (c: Card) => void;
  onClose: () => void;
}

function CardModal({
  isOpen,
  card,
  cardTypes,
  onClose,
  onSubmit,
}: CardModalProps) {
  const { t } = useTranslation();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="auto"
      content={{
        title: card
          ? t("search.passenger.card.edit")
          : t("search.passenger.card.add"),
      }}
    >
      <CardForm
        card={card}
        cardTypes={cardTypes}
        onSubmit={onSubmit}
        onClose={onClose}
      />
    </Modal>
  );
}

interface WheelchairFormProps {
  control: Control<Passenger>;
  wheelchairTypes: WheelchairType[];
}

function WheelchairForm({ control, wheelchairTypes }: WheelchairFormProps) {
  const { t } = useTranslation();

  const type = useWatch({ control, name: "disability.wheelchair.type" });

  return (
    <>
      <div>
        <span className="font-bold text-xs">
          {t("search.passenger.disability.wheelchair.type")}
        </span>
        <Controller
          name="disability.wheelchair.type"
          control={control}
          render={({ field }) => (
            <Select
              className="mt-2"
              value={field.value}
              onChange={field.onChange}
              optional={true}
              options={wheelchairTypes.map((d) => ({
                label: d.name,
                value: d.code,
              }))}
            />
          )}
        />
      </div>

      {type && (
        <>
          <div className="flex flex-col gap-2">
            <span className="font-bold text-xs">
              {t("search.passenger.disability.wheelchair.dimensions") + " *"}
            </span>
            <DimensionsInput control={control} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <span className="font-bold text-xs">
                {t("search.passenger.disability.wheelchair.weight") + " *"}
              </span>
              <div>
                <Controller
                  name="disability.wheelchair.weight"
                  control={control}
                  rules={{
                    required: t("validation.required"),
                  }}
                  render={({ field, fieldState }) => (
                    <Input
                      type="number"
                      value={field.value}
                      onChange={onNumberChange(field.onChange)}
                      placeholder="0"
                      min={0}
                      max={1000}
                      mandatory
                      error={fieldState.invalid}
                      errorMsg={fieldState?.error?.message}
                    />
                  )}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <span className="font-bold text-xs">
                {t("search.passenger.disability.wheelchair.wheelsThickness") +
                  " *"}
              </span>
              <div>
                <Controller
                  name="disability.wheelchair.wheelThickness"
                  control={control}
                  rules={{
                    required: t("validation.required"),
                  }}
                  render={({ field, fieldState }) => (
                    <Input
                      type="number"
                      value={field.value}
                      onChange={onNumberChange(field.onChange)}
                      placeholder="0"
                      min={0}
                      max={1000}
                      mandatory
                      error={fieldState.invalid}
                      errorMsg={fieldState?.error?.message}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function DimensionsInput({ control }: { control: Control<Passenger> }) {
  const { t } = useTranslation();
  return (
    <div className="flex gap-4 items-center">
      <div>
        <Controller
          name="disability.wheelchair.dimensions.width"
          control={control}
          rules={{
            required: t("validation.required"),
          }}
          render={({ field, fieldState }) => (
            <Input
              type="number"
              value={field.value}
              onChange={onNumberChange(field.onChange)}
              placeholder={t("search.passenger.disability.wheelchair.width")}
              min={0}
              max={1000}
              mandatory
              error={fieldState.invalid}
              errorMsg={fieldState?.error?.message}
            />
          )}
        />
      </div>
      <span className="mt-4 self-start">x</span>
      <div>
        <Controller
          name="disability.wheelchair.dimensions.length"
          control={control}
          rules={{
            required: t("validation.required"),
          }}
          render={({ field, fieldState }) => (
            <Input
              type="number"
              value={field.value}
              onChange={onNumberChange(field.onChange)}
              placeholder={t("search.passenger.disability.wheelchair.length")}
              min={0}
              max={1000}
              mandatory
              error={fieldState.invalid}
              errorMsg={fieldState?.error?.message}
            />
          )}
        />
      </div>
      <span className="mt-4 self-start">x</span>
      <div>
        <Controller
          name="disability.wheelchair.dimensions.height"
          control={control}
          rules={{
            required: t("validation.required"),
          }}
          render={({ field, fieldState }) => (
            <Input
              type="number"
              value={field.value}
              onChange={onNumberChange(field.onChange)}
              placeholder={t("search.passenger.disability.wheelchair.height")}
              min={0}
              max={1000}
              mandatory
              error={fieldState.invalid}
              errorMsg={fieldState?.error?.message}
            />
          )}
        />
      </div>
    </div>
  );
}
